import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Plus, Trash2 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { HelpBanner } from '@/components/ui/help-banner'
import type { PaginatedResponse, AssignmentResponse, UserResponse, RoleResponse, ProductResponse, TenantResponse, BookmakerResponse } from '@/api/types'

export function AssignmentsPage() {
  const { t } = useTranslation(['roles', 'common'])
  const navigate = useNavigate()
  const [offset, setOffset] = useState(0)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [form, setForm] = useState({ user_id: '', role_id: '', tenant_id: '', bookmaker_id: '' })
  const queryClient = useQueryClient()
  const limit = 20

  const { data, isLoading } = useQuery({
    queryKey: ['assignments', offset],
    queryFn: () => api.get<PaginatedResponse<AssignmentResponse>>(`/assignments?offset=${offset}&limit=${limit}`),
  })

  const { data: usersData } = useQuery({
    queryKey: ['users-all'],
    queryFn: () => api.get<PaginatedResponse<UserResponse>>('/users?offset=0&limit=500'),
  })
  const { data: rolesData } = useQuery({
    queryKey: ['roles-all'],
    queryFn: () => api.get<PaginatedResponse<RoleResponse>>('/roles?offset=0&limit=100'),
  })
  const { data: productsData } = useQuery({
    queryKey: ['products-all'],
    queryFn: () => api.get<PaginatedResponse<ProductResponse>>('/products?offset=0&limit=100'),
  })
  const { data: tenantsData } = useQuery({
    queryKey: ['tenants-all'],
    queryFn: () => api.get<PaginatedResponse<TenantResponse>>('/tenants?offset=0&limit=100'),
  })
  const { data: bookmakersData } = useQuery({
    queryKey: ['bookmakers-all'],
    queryFn: () => api.get<PaginatedResponse<BookmakerResponse>>('/bookmakers?offset=0&limit=500'),
  })

  const userLookup = Object.fromEntries((usersData?.data.data ?? []).map((u) => [u.id, u.username ?? u.email]))
  const rolesAll = rolesData?.data.data ?? []
  const roleLookup = Object.fromEntries(rolesAll.map((r) => [r.id, r]))
  const productLookup = Object.fromEntries((productsData?.data.data ?? []).map((p) => [p.id, p.name]))
  const tenantLookup = Object.fromEntries((tenantsData?.data.data ?? []).map((t) => [t.id, t.name]))
  const siteLookup = Object.fromEntries((bookmakersData?.data.data ?? []).map((s) => [s.id, s.name]))

  const createMutation = useMutation({
    mutationFn: () => api.post('/assignments', {
      user_id: Number(form.user_id),
      role_id: Number(form.role_id),
      tenant_id: Number(form.tenant_id),
      ...(form.bookmaker_id ? { bookmaker_id: Number(form.bookmaker_id) } : {}),
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assignments'] })
      setDialogOpen(false)
      setForm({ user_id: '', role_id: '', tenant_id: '', bookmaker_id: '' })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/assignments/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['assignments'] }),
  })

  const assignments = data?.data.data ?? []
  const total = data?.data.total ?? 0

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-semibold">{t('nav.assignments', { ns: 'common' })}</h2>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger render={<Button />}>
            <Plus className="h-4 w-4 mr-2" />{t('assignments.newAssignment')}
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t('assignments.createAssignment')}</DialogTitle></DialogHeader>
            <form onSubmit={(e) => { e.preventDefault(); createMutation.mutate() }} className="flex flex-col gap-4">
              <div><Label>{t('labels.userId', { ns: 'common' })}</Label><Input value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })} required /></div>
              <div><Label>{t('labels.roleId', { ns: 'common' })}</Label><Input value={form.role_id} onChange={(e) => setForm({ ...form, role_id: e.target.value })} required /></div>
              <div><Label>{t('labels.tenantId', { ns: 'common' })}</Label><Input value={form.tenant_id} onChange={(e) => setForm({ ...form, tenant_id: e.target.value })} required /></div>
              <div><Label>{t('assignments.bookmakerIdOptional')}</Label><Input value={form.bookmaker_id} onChange={(e) => setForm({ ...form, bookmaker_id: e.target.value })} /></div>
              <Button type="submit" disabled={createMutation.isPending}>{t('buttons.create', { ns: 'common' })}</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <HelpBanner storageKey="assignments">
        <p>{t('assignments.help.line1')}</p>
        <p>{t('assignments.help.line2')}</p>
        <p>{t('assignments.help.line3')}</p>
      </HelpBanner>

      {isLoading ? <p className="text-muted-foreground">{t('loading', { ns: 'common' })}</p> : (
        <>
          <div className="border rounded-md overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b bg-muted/50">
                <th className="text-left p-3 font-medium">{t('labels.user', { ns: 'common' })}</th>
                <th className="text-left p-3 font-medium">{t('labels.role', { ns: 'common' })}</th>
                <th className="text-left p-3 font-medium">{t('labels.product', { ns: 'common' })}</th>
                <th className="text-left p-3 font-medium">{t('labels.tenant', { ns: 'common' })}</th>
                <th className="text-left p-3 font-medium">{t('labels.bookmaker', { ns: 'common' })}</th>
                <th className="text-left p-3 font-medium">{t('labels.created', { ns: 'common' })}</th>
                <th className="p-3"></th>
              </tr></thead>
              <tbody>
                {assignments.map((a) => {
                  const role = roleLookup[a.role_id]
                  const productName = role?.product_id ? (productLookup[role.product_id] ?? `#${role.product_id}`) : '—'
                  return (
                    <tr key={a.id} className="border-b last:border-0 hover:bg-muted/30 cursor-pointer" onClick={() => navigate(`/assignments/${a.id}`)}>
                      <td className="p-3">{userLookup[a.user_id] ?? a.user_id}</td>
                      <td className="p-3 font-medium">{role?.name ?? a.role_id}</td>
                      <td className="p-3">{productName}</td>
                      <td className="p-3">{tenantLookup[a.tenant_id] ?? a.tenant_id}</td>
                      <td className="p-3">{a.bookmaker_id ? (siteLookup[a.bookmaker_id] ?? a.bookmaker_id) : t('labels.all', { ns: 'common' })}</td>
                      <td className="p-3 text-muted-foreground">{new Date(a.created_at).toLocaleDateString()}</td>
                      <td className="p-3"><Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); deleteMutation.mutate(a.id) }}><Trash2 className="h-4 w-4 text-destructive" /></Button></td>
                    </tr>
                  )
                })}
                {assignments.length === 0 && <tr><td colSpan={7} className="p-3 text-center text-muted-foreground">{t('assignments.empty')}</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between mt-4">
            <span className="text-sm text-muted-foreground">{t('labels.total', { ns: 'common' })}: {total}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))}>{t('pagination.previous', { ns: 'common' })}</Button>
              <Button variant="outline" size="sm" disabled={offset + limit >= total} onClick={() => setOffset(offset + limit)}>{t('pagination.next', { ns: 'common' })}</Button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
